import {subDays} from 'date-fns';

export type DateRange = {
  readonly from: string;
  readonly to: string;
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Browser-local calendar date as `YYYY-MM-DD`. */
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
};

/** Parse `YYYY-MM-DD` into a local Date at noon (stable for DatePicker). */
export const parseLocalDateString = (value: string): Date => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
};

/** Default history window: last 30 calendar days through today. */
export const defaultWorkoutDateRange = (now: Date = new Date()): DateRange => {
  const to = toLocalDateString(now);
  const from = toLocalDateString(subDays(now, 30));
  return {from, to};
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Inclusive calendar-day count; same rule as server (UTC midnight of date strings). */
export const inclusiveDayCount = (from: string, to: string): number => {
  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T00:00:00.000Z`);
  return Math.floor((toMs - fromMs) / MS_PER_DAY) + 1;
};

export const MAX_WORKOUT_HISTORY_WINDOW_DAYS = 90;

export const isValidWorkoutDateRange = (from: string, to: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
    return false;
  }
  if (from > to) {
    return false;
  }
  return inclusiveDayCount(from, to) <= MAX_WORKOUT_HISTORY_WINDOW_DAYS;
};
